import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { STATUS_LABELS, TRANSITIONS, VOID_REASON_MIN, type Action, type Me } from '@wo/shared';
import { api, errorText } from '../lib/api';
import { dateTime } from '../lib/format';
import { useMechanics, useSettings } from '../lib/hooks';
import type { AuditRow, WorkOrderDetail } from '../lib/types';
import { Button, Card, ErrorText, Field, Input, Loading, Modal, PageHeader, Select, StatusBadge, Textarea } from '../components/ui';
import { NotesTab, PhotosTab, TimeTab } from './wo-tabs';
import { FieldReportTab } from './forms/FieldReportTab';
import { LubeTab } from './forms/LubeTab';
import { PartsTab } from './forms/PartsTab';
import { RentalTab } from './forms/RentalTab';
import { SignaturesTab } from './forms/SignaturesTab';
import { PrintForms } from '../components/PrintForms';
import { HelpersCard } from './HelpersCard';
import { EmailCustomerButton, FlagsPanel } from './wo-email-flags';

type Tab = 'summary' | 'notes' | 'time' | 'parts' | 'fieldReport' | 'lube' | 'rental' | 'photos' | 'signatures' | 'history' | 'audit';
const TAB_LABELS: Record<Tab, string> = {
  summary: 'Summary',
  notes: 'Notes',
  time: 'Time',
  parts: 'Parts',
  fieldReport: 'Field Report',
  lube: 'Lube',
  rental: 'Rental',
  photos: 'Photos',
  signatures: 'Signatures',
  history: 'History',
  audit: 'Audit',
};

export function WorkOrderPage({ me }: { me: Me }) {
  const { id } = useParams();
  const qc = useQueryClient();
  const wo = useQuery({ queryKey: ['wo', id], queryFn: () => api.get<WorkOrderDetail>(`/api/work-orders/${id}`) });
  const [tab, setTab] = useState<Tab>('summary');
  const [action, setAction] = useState<Action | 'void' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canSeeAudit = me.role === 'ADMIN' || me.role === 'MANAGER';

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['wo', id] });
    qc.invalidateQueries({ queryKey: ['board'] });
    qc.invalidateQueries({ queryKey: ['wo-audit', id] });
    for (const k of ['wo-parts', 'wo-field-report', 'wo-lube', 'wo-rental', 'wo-signatures']) qc.invalidateQueries({ queryKey: [k, Number(id)] });
    qc.invalidateQueries({ queryKey: ['wo-flags', id] });
  };

  if (wo.isLoading) return <Loading />;
  if (wo.error || !wo.data) return <ErrorText error={errorText(wo.error ?? 'Not found')} />;
  const w = wo.data;
  const pendingVoid = w.voidRequests.find((v) => v.status === 'PENDING');
  const readOnly = w.status === 'CLOSED' || w.status === 'VOIDED';
  const openIssues = w.notes.filter((n) => n.kind === 'ADDITIONAL_ISSUE' && !n.resolution).length;
  const isRental = w.closeChecks.some((c) => c.key === 'rentalIn' || c.key === 'rentalOut');
  const tabs: Tab[] = ['summary', 'notes', 'time', 'parts', 'fieldReport', 'lube', ...(isRental ? (['rental'] as Tab[]) : []), 'photos', 'signatures', 'history', ...(canSeeAudit ? (['audit'] as Tab[]) : [])];

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <span className={w.status === 'VOIDED' ? 'line-through' : ''}>Work Order #{w.number}</span>
            <StatusBadge status={w.status} />
          </span>
        }
      >
        {w.allowedActions.map((a) => (
          <Button key={a} variant={a === 'close' ? 'primary' : 'secondary'} onClick={() => { setError(null); setAction(a); }}>
            {TRANSITIONS[a].label}
          </Button>
        ))}
        {me.role !== 'MECHANIC' && w.status !== 'VOIDED' && <EmailCustomerButton woId={w.id} number={w.number} />}
        {w.canRequestVoid && (
          <Button variant="danger" onClick={() => { setError(null); setAction('void'); }}>Request Void</Button>
        )}
      </PageHeader>
      <ErrorText error={error} />

      {pendingVoid && (
        <VoidBanner me={me} request={pendingVoid} onDone={refresh} />
      )}

      <div className="grid gap-4 xl:grid-cols-[1fr_340px]">
        <div className="space-y-4">
          <div className="flex gap-1 overflow-x-auto whitespace-nowrap border-b border-slate-300">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-2 text-sm font-semibold ${tab === t ? 'border-b-2 border-blue-700 text-blue-800' : 'text-slate-600'}`}
              >
                {TAB_LABELS[t]}
                {t === 'notes' && openIssues > 0 && <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-xs text-white">{openIssues}</span>}
              </button>
            ))}
          </div>
          {tab === 'summary' && (
            <div className="space-y-4">
              <Summary w={w} readOnly={readOnly || me.role === 'MECHANIC'} onSaved={refresh} />
              <HelpersCard w={w} canEdit={canSeeAudit && !readOnly} onChange={refresh} />
            </div>
          )}
          {tab === 'notes' && <NotesTab woId={w.id} notes={w.notes} me={me} readOnly={readOnly} onChange={refresh} />}
          {tab === 'time' && <TimeTab woId={w.id} punches={w.punches} me={me} readOnly={readOnly} onChange={refresh} />}
          {tab === 'parts' && <PartsTab woId={w.id} readOnly={readOnly} onChange={refresh} />}
          {tab === 'fieldReport' && <FieldReportTab woId={w.id} me={me} readOnly={readOnly} onChange={refresh} />}
          {tab === 'lube' && <LubeTab woId={w.id} readOnly={readOnly} onChange={refresh} />}
          {tab === 'rental' && <RentalTab w={w} me={me} readOnly={readOnly} onChange={refresh} />}
          {tab === 'signatures' && <SignaturesTab woId={w.id} me={me} readOnly={readOnly} onChange={refresh} />}
          {tab === 'photos' && <PhotosTab files={w.files} />}
          {tab === 'history' && <History w={w} />}
          {tab === 'audit' && <WoAudit id={w.id} />}
        </div>

        <div className="space-y-4">
        <Card title="Close checklist">
          <ul className="space-y-2">
            {w.closeChecks.map((c) => (
              <li key={c.key} className="flex gap-2 text-sm">
                <span className={`font-bold ${c.ok ? 'text-green-700' : 'text-red-700'}`}>{c.ok ? '✓' : '✗'}</span>
                <span>
                  {c.label}
                  {c.detail && <span className="block text-xs text-slate-500">{c.detail}</span>}
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <PrintForms woId={w.id} me={me} />
        {me.role !== 'MECHANIC' && <FlagsPanel woId={w.id} />}
        </div>
      </div>

      {action && action !== 'void' && (
        <ActionDialog w={w} action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); refresh(); }} onError={setError} />
      )}
      {action === 'void' && <VoidDialog w={w} onClose={() => setAction(null)} onDone={() => { setAction(null); refresh(); }} />}
    </>
  );
}

function Summary({ w, readOnly, onSaved }: { w: WorkOrderDetail; readOnly: boolean; onSaved: () => void }) {
  const settings = useSettings();
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({
    problemDescription: w.problemDescription,
    jobType: w.jobType,
    billingType: w.billingType ?? '',
    customerPo: w.customerPo ?? '',
    machineLocation: w.machineLocation ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      await api.patch(`/api/work-orders/${w.id}`, f);
      setEdit(false);
      onSaved();
    } catch (e) {
      setError(errorText(e));
    }
  };
  const billing = settings.data?.billingTypes.find((b) => b.code === w.billingType);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card title="Customer">
        <Link to={`/customers/${w.customer.id}`} className="font-semibold text-blue-800 hover:underline">{w.customer.name}</Link>
        <div className="text-sm text-slate-600">{w.customer.phone}</div>
        {w.customer.contacts.map((c) => (
          <div key={c.id} className="text-xs text-slate-500">{c.name} {c.phone && `· ${c.phone}`}</div>
        ))}
      </Card>
      <Card title="Equipment">
        <Link to={`/machines/${w.machine.id}`} className="font-semibold text-blue-800 hover:underline">{w.machine.make} {w.machine.model}</Link>
        <div className="text-sm text-slate-600">S/N {w.machine.serialNumber}{w.machine.equipmentNo && ` · EQ# ${w.machine.equipmentNo}`}</div>
      </Card>
      <Card
        title="Job"
        className="md:col-span-2"
        actions={!readOnly && !edit && <Button variant="secondary" onClick={() => setEdit(true)}>Edit</Button>}
      >
        {edit ? (
          <div className="space-y-3">
            <ErrorText error={error} />
            <Field label="Details of problem"><Textarea value={f.problemDescription} onChange={set('problemDescription')} /></Field>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Job type">
                <Select value={f.jobType} onChange={set('jobType')}>{settings.data?.jobTypes.map((t) => <option key={t}>{t}</option>)}</Select>
              </Field>
              <Field label="Billing">
                <Select value={f.billingType} onChange={set('billingType')}>
                  <option value="">—</option>
                  {settings.data?.billingTypes.map((b) => <option key={b.code} value={b.code}>{b.code} — {b.label}</option>)}
                </Select>
              </Field>
              <Field label="Customer P/O"><Input value={f.customerPo} onChange={set('customerPo')} /></Field>
              <Field label="Machine location"><Input value={f.machineLocation} onChange={set('machineLocation')} /></Field>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEdit(false)}>Cancel</Button>
              <Button onClick={save}>Save</Button>
            </div>
          </div>
        ) : (
          <dl className="grid gap-x-6 gap-y-2 text-sm md:grid-cols-2">
            <div className="md:col-span-2"><dt className="text-slate-500">Details of problem</dt><dd className="whitespace-pre-wrap">{w.problemDescription}</dd></div>
            <div><dt className="text-slate-500">Job type</dt><dd>{w.jobType}</dd></div>
            <div><dt className="text-slate-500">Billing</dt><dd>{billing ? `${billing.code} — ${billing.label}` : '—'}</dd></div>
            <div><dt className="text-slate-500">Customer P/O</dt><dd>{w.customerPo ?? '—'}</dd></div>
            <div><dt className="text-slate-500">Machine location</dt><dd>{w.machineLocation ?? '—'}</dd></div>
            <div><dt className="text-slate-500">Assigned to</dt><dd>{[w.assignedUser?.name ?? 'Unassigned', ...w.helpers.filter((h) => !h.removedAt).map((h) => h.user.name)].join(' + ')}</dd></div>
            <div><dt className="text-slate-500">Opened</dt><dd>{dateTime(w.openedAt)}</dd></div>
            {w.holdReason && <div><dt className="text-slate-500">On hold</dt><dd>{w.holdReason}{w.holdNote && ` — ${w.holdNote}`}</dd></div>}
            {w.reopenCount > 0 && <div><dt className="text-slate-500">Reopened</dt><dd>{w.reopenCount}×</dd></div>}
            <div>
              <dt className="text-slate-500">Hour meter</dt>
              <dd>{w.meterReadings.length ? w.meterReadings.map((r) => `${Number(r.reading).toFixed(1)}${r.overrideReason ? ` (override: ${r.overrideReason})` : ''}`).join(', ') : '—'}</dd>
            </div>
          </dl>
        )}
      </Card>
    </div>
  );
}

function History({ w }: { w: WorkOrderDetail }) {
  return (
    <Card title="Status history">
      <ol className="space-y-3">
        {w.statusHistory.map((h) => (
          <li key={h.id} className="text-sm">
            <span className="font-semibold">{h.fromStatus ? `${STATUS_LABELS[h.fromStatus]} → ` : ''}{STATUS_LABELS[h.toStatus]}</span>
            <span className="text-slate-500"> · {h.user.name} · {dateTime(h.at)}</span>
            {h.reason && <div className="text-slate-700">“{h.reason}”</div>}
          </li>
        ))}
      </ol>
      <h3 className="mb-2 mt-6 font-semibold">Assignments</h3>
      <ul className="space-y-1 text-sm">
        {w.assignments.map((a) => (
          <li key={a.id}>
            {a.user.name}: {dateTime(a.assignedAt)} – {a.unassignedAt ? dateTime(a.unassignedAt) : 'now'}
            {a.reason && <span className="text-slate-500"> (“{a.reason}”)</span>}
          </li>
        ))}
        {w.assignments.length === 0 && <li className="text-slate-500">Never assigned.</li>}
      </ul>
    </Card>
  );
}

function WoAudit({ id }: { id: number }) {
  const rows = useQuery({ queryKey: ['wo-audit', String(id)], queryFn: () => api.get<AuditRow[]>(`/api/audit?entity=work_order&entityId=${id}`) });
  return (
    <Card title="Audit trail (who changed what)">
      <ul className="space-y-3 text-sm">
        {rows.data?.map((r) => (
          <li key={r.id} className="border-b border-slate-100 pb-2">
            <div><span className="font-semibold">{r.action}</span> · {r.userName ?? 'system'} · {dateTime(r.at)} · seq {r.seq}</div>
            <AuditDiff before={r.beforeJson} after={r.afterJson} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function AuditDiff({ before, after }: { before: unknown; after: unknown }) {
  const b = (before ?? {}) as Record<string, unknown>;
  const a = (after ?? {}) as Record<string, unknown>;
  const norm = (v: unknown) => JSON.stringify(v ?? null);
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].filter((k) => norm(b[k]) !== norm(a[k]));
  if (!keys.length) return null;
  const show = (v: unknown) => (v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));
  return (
    <ul className="mt-1 text-xs text-slate-600">
      {keys.map((k) => (
        <li key={k}>
          <span className="font-mono">{k}</span>: {before ? <><span className="text-red-700 line-through">{show(b[k])}</span> → </> : null}
          <span className="text-green-800">{show(a[k])}</span>
        </li>
      ))}
    </ul>
  );
}

function ActionDialog({
  w,
  action,
  onClose,
  onDone,
  onError,
}: {
  w: WorkOrderDetail;
  action: Action;
  onClose: () => void;
  onDone: () => void;
  onError: (e: string) => void;
}) {
  const rule = TRANSITIONS[action];
  const settings = useSettings();
  const mechanics = useMechanics(Boolean(rule.needsMechanic));
  const [mechanicId, setMechanicId] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [checks, setChecks] = useState<{ label: string }[] | null>(null);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/api/work-orders/${w.id}/transition`, {
        action,
        ...(mechanicId ? { mechanicId: Number(mechanicId) } : {}),
        ...(holdReason ? { holdReason } : {}),
        ...(reason ? { reason } : {}),
      });
      onDone();
    } catch (e) {
      const d = (e as { details?: { checks?: { label: string }[] } }).details;
      if (d?.checks) setChecks(d.checks);
      else {
        onError(errorText(e));
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };

  const valid =
    (!rule.needsMechanic || mechanicId) &&
    (!rule.needsHoldReason || (holdReason && (holdReason !== 'Other' || reason.trim().length >= 3))) &&
    (!rule.needsReason || reason.trim().length >= 3);

  return (
    <Modal
      title={`${rule.label} — #${w.number}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || busy}>{rule.label}</Button>
        </>
      }
    >
      {checks && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">
          <p className="font-semibold">Not yet — these still need doing:</p>
          <ul className="list-disc pl-5">{checks.map((c) => <li key={c.label}>{c.label}</li>)}</ul>
        </div>
      )}
      {rule.needsMechanic && (
        <Field label="Mechanic" required>
          <Select value={mechanicId} onChange={(e) => setMechanicId(e.target.value)}>
            <option value="">Choose…</option>
            {mechanics.data?.filter((m) => m.id !== w.assignedUserId).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </Select>
        </Field>
      )}
      {rule.needsHoldReason && (
        <Field label="Hold reason" required>
          <Select value={holdReason} onChange={(e) => setHoldReason(e.target.value)}>
            <option value="">Choose…</option>
            {settings.data?.holdReasons.map((r) => <option key={r}>{r}</option>)}
          </Select>
        </Field>
      )}
      {(rule.needsReason || rule.needsHoldReason) && (
        <Field label={rule.needsReason ? 'Reason' : 'Note'} required={rule.needsReason || holdReason === 'Other'}>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      )}
      {!rule.needsMechanic && !rule.needsReason && !rule.needsHoldReason && !checks && <p className="text-sm">Confirm: {rule.label}?</p>}
    </Modal>
  );
}

function VoidDialog({ w, onClose, onDone }: { w: WorkOrderDetail; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    try {
      await api.post(`/api/work-orders/${w.id}/void-requests`, { reason });
      onDone();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal
      title={`Request void — #${w.number}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={submit} disabled={reason.trim().length < VOID_REASON_MIN}>Send request</Button>
        </>
      }
    >
      <p className="text-sm text-slate-600">The number is kept and the job stays visible. Someone else must approve.</p>
      <ErrorText error={error} />
      <Field label={`Reason (at least ${VOID_REASON_MIN} characters)`} required>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}

function VoidBanner({ me, request, onDone }: { me: Me; request: WorkOrderDetail['voidRequests'][number]; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const canDecide = (me.role === 'ADMIN' || me.role === 'MANAGER') && me.id !== request.requestedBy;
  const decide = async (approve: boolean) => {
    try {
      await api.post(`/api/void-requests/${request.id}/decision`, { approve });
      onDone();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm">
      <p><span className="font-semibold">Void requested:</span> “{request.reason}” · {dateTime(request.createdAt)}</p>
      <ErrorText error={error} />
      {canDecide ? (
        <div className="mt-2 flex gap-2">
          <Button variant="danger" onClick={() => decide(true)}>Approve void</Button>
          <Button variant="secondary" onClick={() => decide(false)}>Deny</Button>
        </div>
      ) : (
        <p className="mt-1 text-slate-600">Waiting for the other approver.</p>
      )}
    </div>
  );
}
